import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Car, Bike, Bus, Truck, Cable, BusFront, Network } from 'lucide-react';
import { LiaTruckPickupSolid } from "react-icons/lia";
import { PiVan } from "react-icons/pi";

const VEHICLE_CATEGORIES = [
  {
    title: 'Personal Vehicles',
    types: [
      { value: 'Car', label: 'Car', icon: Car, description: 'Standard 4-wheel personal vehicle' },
      { value: 'SUV', label: 'SUV', icon: Car, description: 'Sport utility vehicle for all terrains' },
      { value: 'Bike', label: 'Bike', icon: Bike, description: 'Two-wheeler personal transport' }
    ]
  },
  {
    title: 'Commercial Vehicles',
    types: [
      { value: 'Bus', label: 'Bus', icon: Bus, description: 'Large passenger transport vehicle' },
      { value: 'Lorry', label: 'Lorry', icon: Truck, description: 'Heavy goods transport vehicle' },
      { value: 'Van', label: 'Van', icon: PiVan, description: 'Commercial delivery vehicle' }
    ]
  },
  {
    title: 'Special Vehicles',
    types: [
      { value: 'Pickup', label: 'Pickup', icon: LiaTruckPickupSolid, description: 'Light duty cargo vehicle' },
      { value: 'Three Wheel', label: 'Three Wheel', icon: Network, description: 'Three-wheeled utility vehicle' }
    ]
  }
];

function TypeSelect({ filterByType }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value) => {
    setSelectedType(value);
    filterByType(value);
    setIsOpen(false);
  };

  return (
    <div className="relative w-full sm:w-60 md:w-72" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                 flex items-center justify-between
                 hover:border-[#6F48E5] transition-all duration-300
                 focus:ring-[#6F48E5] focus:border-[#6F48E5] focus:outline-none p-2.5"
      >
        <div className="flex items-center gap-2">
          <BusFront 
            size={18} 
            className="text-[#6F48E5] hidden sm:block" 
          />
          <span className="truncate font-semibold ">
            {selectedType || 'Choose a Type'}
          </span>
        </div>
        <ChevronDown 
          size={18} 
          className={`text-gray-500 transition-transform duration-300 
                   ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-lg 
                      border border-gray-300 overflow-hidden
                       sm:left-auto
                      w-full sm:w-[320px] md:w-[480px] lg:w-[640px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
            {VEHICLE_CATEGORIES.map((category, idx) => (
              <div key={idx} className="space-y-2">
                <h3 className="text-[#6F48E5] font-medium text-sm border-b border-gray-200 pb-1">
                  {category.title}
                </h3>
                <div className="space-y-1">
                  {category.types.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => handleSelect(type.value)}
                      className={`w-full p-2 rounded-md flex items-start gap-2 text-sm
                               transition-all duration-300 hover:bg-[#6F48E5]/5
                               ${selectedType === type.value ? 'bg-[#6F48E5]/10' : ''}`}
                    >
                      <type.icon 
                        size={16} 
                        className="text-[#6F48E5] mt-0.5 shrink-0" 
                      />
                      <div className="text-left min-w-0">
                        <div className="font-medium text-gray-800 truncate">
                          {type.label}
                        </div>
                        <div className="text-xs text-gray-500 line-clamp-1">
                          {type.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-gray-50 p-2 border-t border-gray-200">
            <button
              onClick={() => handleSelect('All Vehicle Types')}
              className="text-sm text-[#6F48E5] hover:text-[#6F48E5]/80 font-medium 
                       transition-colors duration-300"
            >
              View All Types
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TypeSelect;